/*eslint-disable*/

/**
 * COMMON CALCULATIONS
 */

/**
 * Capital Cost reduction is the combined user entered amounts from the "Personalize My Payment" Panel
 * @param downPaymentAmount - The amount the user has enter for a Down Payment - Entered by the user
 * @param tradeInValue - The amount The user has entered as the Trade in Value for their current vehicle - Entered by the user
 * @param retailerAccessoryCost - The amount the user expects to add in Retailer Acccessories - Entered by the user
 * @returns {number} - The total amount from the user entered values
 */
export const calculateCapitalCostReduction = (downPaymentAmount = 0, tradeInValue = 0, retailerAccessoryCost = 0) => {
  const capitalCostReduction = (downPaymentAmount + tradeInValue) - retailerAccessoryCost;
  return capitalCostReduction;
};

/**
 * Adjusted Capital cost is the result of subtracting the Capital Cost Reduction from the Total Gross Capitalized Amount
 * @param totalGrossCapitalizedAmount - the Popularly Equipped price (msrp) plus the Acquisition Fee - Calculated Here
 * @param capitalizedCostReduction - The Combined values from the User Input areas - Calculated Here
 * @returns {number} - The Adjusted Capitalized Cost
 */
export const calculateAdjustedCapitalCost = (totalGrossCapitalizedAmount, capitalizedCostReduction) => {
  const adjustedCapitalizedCost = totalGrossCapitalizedAmount - capitalizedCostReduction;
  return adjustedCapitalizedCost;
};

/**
 * LEASE CALCULATIONS
 */

/**
 * The Residual Value is the MSRP (HEre we are using the Popularly Equipped Price because the user can not enter packages)
 * multiplied by the Residual PCT which is sent in from the Lease Incentive
 * @param popularEquippedPrice - The most popularly used MSRP value - Comes from the Trim Data
 * @param residualPCT - The Residual Percentage of the lease - Comes from the Lease Incentices
 * @returns {number} - The Residual Value of the vehicle
 */
export const calculateResidualValue = (popularEquippedPrice, residualPCT) => {
  const residualValue = popularEquippedPrice * residualPCT;
  return residualValue;
};

/**
 * The Gross Capitalized Amount is the value of the MSRP (HEre we are using the Popularly Equipped Price because the user can not enter packages)
 * and the acquisition fee from the dealership
 * @param popularEquippedPrice - The most popularly used MSRP value - Comes from the Trim Data
 * @param acquisitionFee The Dealers Aquisition Fee -- Comes from the Lease Incentives or the Lease Offer Data
 * @returns {*}
 */
export const calculateTotalGrossCapitalizedAmount = (popularEquippedPrice, acquisitionFee) => {
  const totalGrossCapitalizedAmount = (popularEquippedPrice + acquisitionFee);
  return totalGrossCapitalizedAmount
};

/**
 * The Vehicle Deprication is the reduction in the value of the vehicle over time.
 * @param adjustedCapitalizedCost - The Gross Capitalized Cost minus the User Entered Values - Calculated here
 * @param residualValue - The Value remaining at the end of the lease - Calculated Here
 * @param leaseMonths - The term of the Lease - User Entered
 * @returns {number} - The amount the vehicle is expected to depricate over one month
 */
export const calculateVehicleDeprecation = (adjustedCapitalizedCost, residualValue, leaseMonths) => {
  const deprecation = (adjustedCapitalizedCost - residualValue) / leaseMonths;
  return deprecation;
};

/**
 * The Monthly Rent is the amount charged per month for use of the Dealership owned vehicle
 * Adjusted Money Factor is based upon a hardcoded value inside the Infiniti Code.
 * @param adjustedCapitalizedCost - A value found in the previous code, unsure of origins
 * @param residualValue - The amount remaining at the end of the lease - Calculated here
 * @param moneyFactor - Value sent in from the Lease Incentive
 * @param brand - in or ni (Nissan or Infiniti, entered based on environment)
 * @returns {number} - The Monthly Rent amount
 */
export const calculateMonthlyRent = (adjustedCapitalizedCost, residualValue, moneyFactor, brand) => {
  const adjustedMoneyFactor = (brand === 'in') ? 0.00035 : 0;
  const monthlyRent = (adjustedCapitalizedCost + residualValue) * (moneyFactor + adjustedMoneyFactor);
  return monthlyRent;
};

/**
 * The Monthly Payment on a Lease Calculated from the Incentive Data
 * @param vehicleDeprication - The amount the vehicle is expected to reduce over the course of the 1 month - Calculated Here
 * @param monthlyRent - The amount charged per month  - Calculated Here
 * @returns {number} - The Monthly Payment
 */
export const calculateLeaseIncentiveMonthlyPayment = (vehicleDeprication, monthlyRent) => {
  const monthlyPayment = vehicleDeprication + monthlyRent;
  return Math.ceil(monthlyPayment);
};

/**
 * The amount due at the signing of the lease. Equal to the Down Payment Amount and one months Payment
 * @param downPaymentAmount - The Down Payment amount - either entered in the User input or pulled from the LEase Offer Data
 * @param monthlyPayment - The amount expected to be payed per month - Calculated here or pulled from Lease Ofer Data
 * @returns {number} - the anount due at signing
 */
export const calculateLeaseDueAtSigning = (downPaymentAmount, monthlyPayment) => {
  const dueAtSigning = downPaymentAmount + monthlyPayment;
  return dueAtSigning;
};

/**
 * Finance Calculations CALCULATIONS
 */

/**
 * Need to take in User Values as well
 *
 * For Purchase Offers, Cashback is only calculated if there is a cashback offer. Otherwise it is 0. The Estimated amount is
 * Equal to the MSRP (Popularly Equipped Price) minus the Cashback amount
 * @param popularlyEquippedPrice - The most popularly used MSRP value - Comes from the Trim Data
 * @param cashbackAmount - 0 or equal to the cashback offer
 * @returns {number} - The Estimated amount financed
 */
export const calculateEstimatedAmountFinance = (popularlyEquippedPrice, capitalCostReduction, cashbackAmount = 0) => {
  const estimatedAmountFinanced = popularlyEquippedPrice - cashbackAmount - capitalCostReduction;
  return estimatedAmountFinanced;
};

/**
 * The Annual Percentage Rate converted to a decimal value
 * @param annualPercentageRate - The APR, comes in either from Finance offer data or Purchase Incentive Data
 * @returns {number} - The Loan Rate
 */
export const calculateLoanRate = (annualPercentageRate) => {
  const loanRate = Number((annualPercentageRate / 100).toFixed(4));
  // const loanRate = annualPercentageRate / 100;
  return loanRate;
};

/**
 * Calculate the finance payment option monthly payments based on the loan rate, term and amount financed.
 * Equation coming from http://teachertech.rice.edu/Participants/bchristo/lessons/carpaymt.html
 * @param loanRate - The Rate of the Loan, calculated here
 * @param estimatedAmountFinanced - The estimated amount financed in the loam - Calculated Here
 * @param financedMonths - The term of the loan in months
 * @returns {*} - The Monthly Loan Payment
 */
export const calculateFinanceMonthlyPayment = (loanRate, estimatedAmountFinanced, financedMonths) => {
  let monthlyPayment;
  if (loanRate === 0) {
    monthlyPayment = estimatedAmountFinanced / financedMonths;
  } else {
    const principal = estimatedAmountFinanced;
    const interestRate = loanRate;
    const months = financedMonths;
    monthlyPayment = ( principal * ( interestRate / 12 ) ) / ( 1 - Math.pow(1 + ( interestRate / 12 ), ( -1 * months ) ) );
  }
  return Math.ceil(monthlyPayment);
};



/*eslint-enable*/



/** WEBPACK FOOTER **
 ** ./src/business-logic/calculations.js
 **/